import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

import styled from "styled-components";

// ---------- Styled Components ----------
const MainPage = styled.div`
  position: relative;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  &:hover {
    cursor: help;
  }
`;

const BgImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
`;

// Resolves only once the browser has fully decoded the image, so swapping
// it in never shows a blank/white frame mid-decode.
function preloadImage(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.src = src;
    const done = () => resolve(img);
    if (img.decode) {
      img.decode().then(done).catch(done);
    } else {
      img.onload = done;
      img.onerror = done;
    }
  });
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const Header = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 4vh;
  background: rgba(0, 0, 0, 1);
`;

const Nina = styled.div`
  font-family: Roumald;
  font-size: 1.2em;
  color: white;
  text-transform: lowercase;
  //cursor: pointer;
  z-index: 100;
  vertical-align: middle;
  height: 100%;
  display: flex;
  align-items: center;
  left: 3vw;
  position: absolute;
`;

const TitleWrapper = styled.div`
  position: absolute;
  bottom: 6vh;
  left: 3vw;
  z-index: 100;

  width: 25vw;
`;

const ProjectTitle = styled.div`
  font-family: Roumald;
  font-size: 1.1em;
  color: white;
  text-transform: lowercase;
  line-height: 1.2;
  cursor: pointer;
  background: rgba(139, 139, 139, 0.7);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 0.2em 0.75em;
  // padding-left: 0.5em;

  &:hover {
    text-decoration: underline;
  }

  ${({ active }) =>
    active &&
    `
      color: blue;
    `}// @media (max-width: 1600px) {
  //   font-size: 1.2em;
  // }
`;

const Type = styled.div`
  font-family: Roumald;
  font-size: 1em;
  color: white;
  text-transform: lowercase;
  position: absolute;
  left: 3vw;
  top: 20vh;
  margin-bottom: 0.5em;
  background: rgba(104, 103, 103, 0.7);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 0.2em 0.75em;
  display: none;
`;

const ProjectInfoWrapper = styled.div`
  position: absolute;
  top: 20vh;
  left: 30vw;

  display: none;
  flex-direction: column;
`;

const ProjectInfo = styled.div`
  font-family: Roumald;
  font-size: 1em;
  color: white;
  text-transform: lowercase;

  background: rgba(104, 103, 103, 0.7);
  backdrop-filter: blur(10px);
  padding: 0.2em 0.75em;
  border-radius: 15px;
  width: max-content;
`;

const CaptionWrapper = styled.div`
  position: absolute;
  top: 20vh;
  left: 60vw;
  width: max-content;
  z-index: 100;
  display: none;
`;

const Caption = styled.div`
  font-family: Roumald;
  font-size: 1em;
  line-height: 1.4;
  color: white;
  text-align: left;
  white-space: pre-line;
  text-transform: lowercase;
  background: rgba(104, 103, 103, 0.7);
  backdrop-filter: blur(10px);
  padding: 0.2em 0.75em;
  border-radius: 15px;
  // margin-bottom: 1em;

  &:hover {
    cursor: pointer;
  }
`;

const Credits = styled(Caption)`
  display: none;
  font-family: Roumald;
  font-size: 1em;
  line-height: 1.4;
  color: white;
  text-align: left;
  white-space: pre-line;
  text-transform: lowercase;
  background: rgba(104, 103, 103, 0.7);
  backdrop-filter: blur(10px);
  padding: 0.2em 0.75em;
  border-radius: 15px;
  width: 30vw;
`;

const About = styled(Link)`
  font-family: Roumald;
  font-size: 1.2em;
  color: white;
  text-transform: lowercase;
  top: 0;
  height: 100%;
  display: flex;
  align-items: center;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const AboutWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1em;
  position: absolute;
  right: 3vw;
  top: 0;
  height: 100%;
`;

const AboutSectionWrapper = styled.div`
  position: absolute;
  top: 10vh;
  right: 3vw;
  width: 30vw;
  z-index: 100;
  display: none;
`;

const AboutSection = styled.div`
  font-family: Roumald;
  font-size: 1em;
  color: white;
  text-transform: lowercase;
  line-height: 1.5;
  background: rgba(104, 103, 103, 0.7);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 0.5em 1em;
`;

const MainContent = styled.div`
  display: block;
`;

// ---------- Component ----------
export default function Main() {
  const [projects, setProjects] = useState([]);
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [imageIndex, setImageIndex] = useState(0);
  const [caption, setCaption] = useState(false);
  const captionRef = useRef(null);
  const infoRef = useRef(null);
  const creditRef = useRef(null);
  const typeRef = useRef(null);
  const aboutSectionRef = useRef(null);
  const aboutSectionRef2 = useRef(null);
  const aboutSectionWrapperRef = useRef(null);
  const mainContentRef = useRef(null);
  var [bio1, setBio1] = useState("");
  var [bio2, setBio2] = useState("");
  // Two stacked <img> layers we swap between, so switching images never
  // relies on a background-image swap (which can flash white on WebKit
  // while the GPU uploads the new texture).
  const layerARef = useRef();
  const layerBRef = useRef();
  const activeLayerRef = useRef("A");
  const preloadedRef = useRef([]);

  // Load projects on mount
  useEffect(() => {
    async function loadProjects() {
      const response = await fetch(
        "https://wordpress.ninakunzendorf.net/wp-json/wp/v2/project?acf_format=standard",
      );
      if (!response.ok) return;

      const loadedProjects = await response.json();
      console.log(`${loadedProjects.length} projects loaded`);

      const cleaned = cleanProjects(loadedProjects);
      const sorted = sortProjects(cleaned);

      setProjects(sorted);

      // pick a random project initially
      // const randomIndex = Math.floor(Math.random() * sorted.length);
      setCurrentProjectIndex(0);

      const project = sorted[0];
      setCaption();
      // `${project.abstract}\n${project.credits}\n${project.location}`
    }

    loadProjects();
  }, []);

  useEffect(() => {
    // Fetch wordpress post with bio for about section
    async function loadBio() {
      const response = await fetch(
        "https://wordpress.ninakunzendorf.net/wp-json/wp/v2/pages?_embed",
      );
      if (!response.ok) {
        // oups! something went wrong
        console.log("nothing fetched");
        return;
      }
      var bioPost = await response.json();
      bioPost = bioPost.find((item) => item.title.rendered === "bio");
      console.log(bioPost);
      //  setBgImageSrc(bgImagePost._embedded["wp:featuredmedia"][0].source_url);
      setBio1(bioPost.acf.bio1);
      setBio2(bioPost.acf.bio2);
      console.log(bio1);
    }
    loadBio();
  }, []);

  // Preload + fully decode every slide of the current project up front,
  // keeping references so Safari doesn't garbage-collect (and abort) the
  // in-flight loads.
  useEffect(() => {
    const project = projects[currentProjectIndex];
    if (!project) return;
    let cancelled = false;
    Promise.all(project.images.map(preloadImage)).then((imgs) => {
      if (!cancelled) preloadedRef.current = imgs;
    });
    return () => {
      cancelled = true;
    };
  }, [projects, currentProjectIndex]);

  // Swap the visible background only once the target image has actually
  // loaded, using two stacked <img> layers instead of background-image.
  useEffect(() => {
    const project = projects[currentProjectIndex];
    const src = project?.images[imageIndex];
    if (!src) return;

    const showingA = activeLayerRef.current === "A";
    const activeEl = showingA ? layerARef.current : layerBRef.current;
    const nextEl = showingA ? layerBRef.current : layerARef.current;
    if (!nextEl || (activeEl && activeEl.src === src)) return;

    const reveal = () => {
      nextEl.style.opacity = "1";
      if (activeEl) activeEl.style.opacity = "0";
      activeLayerRef.current = showingA ? "B" : "A";
    };

    if (nextEl.src === src && nextEl.complete) {
      reveal();
    } else {
      nextEl.onload = reveal;
      nextEl.src = src;
    }
  }, [projects, currentProjectIndex, imageIndex]);

  // Cycle through project images on click
  function changeImage(e) {
    e.stopPropagation();
    const project = projects[currentProjectIndex];

    if (project.images[imageIndex + 1]) {
      setImageIndex(imageIndex + 1);
    } else {
      setImageIndex(0); // loop back to first image
    }
  }

  // Clean projects for easier handling
  function cleanProjects(loadedProjects) {
    return loadedProjects.map((p) => ({
      title: p.title.rendered,
      abstract: p.acf.projectAbstract,
      year: p.acf.year,
      location: p.acf.location,
      quote: p.acf.quote,
      credits: p.acf.credits,
      role: p.acf.role,
      type: p.acf.type,
      images: [
        p.acf.image1,
        p.acf.image2,
        p.acf.image3,
        p.acf.image4,
        p.acf.image5,
        p.acf.image6,
      ].filter(Boolean), // removes null/undefined images
    }));
  }

  // Sort projects by year descending
  function sortProjects(projects) {
    return [...projects].sort((a, b) =>
      a.year.substring(a.year.length - 4) < b.year.substring(b.year.length - 4)
        ? 1
        : -1,
    );
  }

  // Change Caption field to project info on project title click
  function handleProjectClick(index) {
    setCurrentProjectIndex(index);
    setImageIndex(0);

    infoRef.current.style.display = "block";
    captionRef.current.parentElement.style.display = "block";
    creditRef.current.style.display = "block";
    typeRef.current.style.display = "block";

    const project = projects[index];
    setCaption(
      `${project.abstract}\n${project.credits}\n\n${project.location}`,
    );
  }

  function showCaption(e) {
    e.stopPropagation();
    if (caption) {
      captionRef.current.style.width = "30vw";
      captionRef.current.innerText = projects[currentProjectIndex].abstract;
      creditRef.current.innerText = projects[currentProjectIndex].credits;
      creditRef.current.style.display = "block";
      setCaption(false);
    } else {
      captionRef.current.style.width = "max-content";
      captionRef.current.innerText = "+ read more";
      creditRef.current.style.display = "none";
      setCaption(true);
    }
  }

  function hideCaption(e) {
    e.stopPropagation();

    captionRef.current.style.width = "max-content";
    captionRef.current.innerText = "+ read more";
    creditRef.current.style.display = "none";
    setCaption(true);
  }

  // Guard: don't render until projects exist
  if (projects.length === 0) {
    return <div>Loading...</div>;
  }

  function openAbout() {
    console.log(aboutSectionWrapperRef.current.style.display);
    if (aboutSectionWrapperRef.current.style.display === "none") {
      console.log("opening about");
      aboutSectionWrapperRef.current.style.display = "block";
      mainContentRef.current.style.display = "none";
    } else {
      aboutSectionWrapperRef.current.style.display = "none";
      mainContentRef.current.style.display = "block";
    }
  }

  function showFullAbout(e) {
    e.stopPropagation();

    if (aboutSectionRef2.current.innerText === "+ read more") {
      aboutSectionRef2.current.innerText = bio2;
    } else {
      aboutSectionRef2.current.innerText = "+ read more";
      aboutSectionWrapperRef.current.style.display = "none ";
      mainContentRef.current.style.display = "block";
    }
  }
  const currentProject = projects[currentProjectIndex];

  return (
    <MainPage onClick={changeImage}>
      <BgImage
        ref={layerARef}
        src={currentProject.images[imageIndex]}
        alt=""
        style={{ opacity: 1 }}
      />
      <BgImage ref={layerBRef} alt="" style={{ opacity: 0 }} />
      <Wrapper>
        <Header>
          <Nina>nina kunzendorf</Nina>
          <AboutWrapper>
            <About onClick={openAbout}>about</About>
            <About to="mailto:ninakunzendorf@gmail.com">contact</About>
          </AboutWrapper>
        </Header>
        <MainContent ref={mainContentRef}>
          <Type ref={typeRef}>{currentProject.role}</Type>

          <TitleWrapper>
            {projects.map((item, key) => (
              <ProjectTitle
                key={key}
                active={key === currentProjectIndex}
                onMouseEnter={() => setHoveredIndex(key)}
                onMouseLeave={() => setHoveredIndex(null)}
                onClick={(e) => {
                  setCurrentProjectIndex(key);
                  setImageIndex(0);
                  handleProjectClick(key);
                  hideCaption(e);
                }}
              >
                {hoveredIndex === key ? `${item.type}` : item.title}
              </ProjectTitle>
            ))}
          </TitleWrapper>
          <ProjectInfoWrapper ref={infoRef}>
                        <ProjectInfo>{currentProject.type}</ProjectInfo>
            <ProjectInfo>{currentProject.year}</ProjectInfo>
            <ProjectInfo>{currentProject.location}</ProjectInfo>
          </ProjectInfoWrapper>
          <CaptionWrapper>
            <Caption ref={captionRef} onClick={showCaption}>
              + read more
            </Caption>
            <Credits ref={creditRef} onClick={hideCaption}></Credits>
          </CaptionWrapper>
        </MainContent>
        <AboutSectionWrapper
          ref={aboutSectionWrapperRef}
          style={{ display: "none" }}
        >
          <AboutSection ref={aboutSectionRef} onClick={showFullAbout}>
            {bio1}
          </AboutSection>
          <AboutSection ref={aboutSectionRef2} onClick={showFullAbout}>
            + read more
          </AboutSection>
        </AboutSectionWrapper>
      </Wrapper>
    </MainPage>
  );
}
